import { Bouton } from './composants/bouton'
import { Badge } from './composants/badge'
import { useState } from 'react'
import { CHAMP } from './synchro'
import type { Reglement } from '../db/modele'
import { eurosVersCentimes, formatEuro } from '../lib/money'

const dateFr = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR')

/**
 * Suivi du paiement d'un document émis : « Payée le … » avec retrait, ou date d'encaissement (aujourd'hui par
 * défaut, de la date d'émission à aujourd'hui) et « Marquer payée ». Ne modifie jamais le document : seule la date
 * d'encaissement est notée par le serveur.
 */
export function Paiement(props: {
  /** Libellé accessible du document, par exemple « la facture FAC-2026-0001 ». */
  libelle: string
  emisLe: string
  payeLe?: string
  occupe: boolean
  marquer: (date: string) => void
  retirer: () => void
  /** Règlements partiels déjà notés. */
  reglements?: Reglement[]
  /** Reste dû en euros TTC (règlements partiels déjà déduits) : active la saisie d'un règlement partiel. */
  reste?: number
  regler?: (date: string, montantCents: number) => void
  retirerReglement?: (id: string) => void
}) {
  const aujourdhui = new Date().toISOString().slice(0, 10)
  const [date, setDate] = useState(aujourdhui)
  const [montant, setMontant] = useState('')
  const cents = eurosVersCentimes(montant)
  const resteCents = props.reste === undefined ? null : Math.round(props.reste * 100)
  const partielValide = cents !== null && cents > 0 && resteCents !== null && cents < resteCents
  return (
    <div className="flex flex-col gap-2">
      {props.reglements?.map((r) => (
        <p key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
          <span>
            Règlement de {formatEuro(r.montantCents / 100)} le {dateFr(r.date)}
          </span>
          {!props.payeLe && props.retirerReglement && (
            <Bouton variante="lien" onClick={() => props.retirerReglement!(r.id)} disabled={props.occupe} aria-label={`Retirer le règlement de ${formatEuro(r.montantCents / 100)} du ${dateFr(r.date)}`}>
              Retirer
            </Bouton>
          )}
        </p>
      ))}
      {!props.payeLe && props.reglements?.length ? <p className="text-sm font-semibold">Reste dû : {formatEuro(props.reste ?? 0)}</p> : null}
    <div className="flex flex-wrap items-center gap-2">
      {props.payeLe ? (
        <>
          <Badge statut="succes">Payée le {dateFr(props.payeLe)}</Badge>
          <Bouton variante="lien" onClick={props.retirer} disabled={props.occupe}>
            Retirer le paiement
          </Bouton>
        </>
      ) : (
        <>
          <input
            type="date"
            aria-label={`Date du paiement de ${props.libelle}`}
            className={`${CHAMP} w-auto`}
            value={date}
            min={props.emisLe.slice(0, 10)}
            max={aujourdhui}
            onChange={(e) => setDate(e.target.value)}
          />
          <Bouton variante="secondaire" onClick={() => props.marquer(date)} disabled={props.occupe || !date}>
            Marquer payée
          </Bouton>
        </>
      )}
    </div>
      {!props.payeLe && props.regler && props.reste !== undefined && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            inputMode="decimal"
            aria-label={`Montant du règlement partiel de ${props.libelle} (€)`}
            placeholder="Montant reçu (€)"
            className={`${CHAMP} w-40`}
            value={montant}
            onChange={(e) => setMontant(e.target.value)}
          />
          <Bouton
            variante="secondaire"
            disabled={props.occupe || !date || !partielValide}
            onClick={() => {
              props.regler!(date, cents!)
              setMontant('')
            }}
          >
            Règlement partiel
          </Bouton>
        </div>
      )}
    </div>
  )
}
